import { useEffect, useMemo, useRef, useState } from "react";
import { useAppTheme } from "../../context/ThemeContext";
import ThemeChoiceDialog from "../../components/ThemeChoiceDialog";
import {
  CheckCircle,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Moon,
  Sun,
  LogOut,
  Menu,
  Search,
  X,
  AlertTriangle,
  Bell,
} from "lucide-react";
import {
  AdminPushMenuControls,
  AdminPushNotificationPrompt,
  adminReceivesWebsiteRequestPush,
} from "../../components/PushNotifications";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import { AdminBreadcrumb } from "./components/AdminUi";

const LOGO_FULL_URL = "/images/logo-full.png";
const LOGO_MARK_URL = "/images/logo.png";

function getInitials(name, email) {
  if (name && name.trim()) {
    return name
      .split(/\s+/)
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  }
  if (email) return email.slice(0, 2).toUpperCase();
  return "A";
}

function scopeLabel(scope) {
  if (scope === "crm_operatore") return "Operatore CRM";
  return "Super Admin";
}

function SidebarBrand({ collapsed }) {
  return (
    <div className={`shrink-0 border-b border-admin-border pt-4 pb-3 ${collapsed ? "px-1.5" : "px-4"}`}>
      <div className={`flex h-[64px] items-center justify-center ${collapsed ? "" : "flex-col"}`}>
        <img
          src={collapsed ? LOGO_MARK_URL : LOGO_FULL_URL}
          alt="Affidya"
          className={collapsed ? "h-8 w-8 object-contain" : "h-11 w-auto"}
        />
        {!collapsed ? (
          <p className="mt-1.5 inline-flex h-[14px] items-center rounded-full bg-admin-accent px-2.5 text-[11px] font-extrabold uppercase leading-none tracking-wide text-admin-accent-fg">
            Amministrazione
          </p>
        ) : null}
      </div>
    </div>
  );
}

function formatMenuCount(count) {
  const value = Number(count) || 0;
  if (value < 1) return "";
  return value > 99 ? "99+" : String(value);
}

function SidebarNav({ sections, activeModule, onModuleClick, collapsed = false, highlightPrimary = false }) {
  const firstSecondaryIdx = sections.findIndex((section) => !section.primary && section.items?.length);
  return (
    <nav aria-label="Menu amministrazione" className={`sidebar-scroll flex-1 overflow-y-auto ${collapsed ? "px-2 pt-5 pb-2" : "px-2.5 pt-3 pb-2"}`}>
      {sections.map((section, sectionIdx) => {
        if (!section.items?.length) return null;
        const isPrimaryBlock = highlightPrimary && section.primary;
        const titleId = `admin-nav-group-${section.id || sectionIdx}`;
        return (
          <div key={section.id || section.title || `section-${sectionIdx}`}>
            {sectionIdx === firstSecondaryIdx && sectionIdx > 0 ? (
              <div className={collapsed ? "my-3 border-t border-admin-border" : "mb-1 mt-5 border-t border-admin-border px-3 pt-3 text-[10px] font-semibold uppercase tracking-wider text-admin-muted/80"}>
                {collapsed ? null : "Gestione piattaforma"}
              </div>
            ) : null}
            <div
              role="group"
              aria-labelledby={titleId}
              className={`${sectionIdx > 0 ? (section.primary ? "mt-2" : "mt-3") : ""} ${
                isPrimaryBlock ? (collapsed ? "rounded-2xl bg-admin-highlight py-1.5" : "rounded-2xl bg-admin-highlight px-1 py-2") : ""
              }`}
            >
              <div
                id={titleId}
                className={collapsed ? "sr-only" : `mb-1 px-3 text-[10.5px] font-bold uppercase tracking-wider ${section.primary ? "text-admin-accent" : "text-admin-muted"}`}
              >
                {section.title}
              </div>
              {section.items.map((item) => {
                const Icon = item.icon;
                const isActive = activeModule === item.id;
                const countLabel = formatMenuCount(item.count);
                const countPhrase = item.countTitle || `nuov${countLabel === "1" ? "a" : "e"}`;
                const title = countLabel ? `${item.label} (${countLabel} ${countPhrase})` : item.label;
                return (
                  <button
                    key={item.id}
                    type="button"
                    title={title}
                    aria-current={isActive ? "page" : undefined}
                    data-testid={`admin-nav-${item.id}`}
                    onClick={() => onModuleClick(item.id)}
                    className={`relative mb-0.5 flex w-full items-center rounded-xl text-left text-sm font-medium transition-colors ${
                      collapsed ? "h-10 justify-center" : "gap-3 px-3 py-2"
                    } ${
                      isActive
                        ? "bg-admin-surface font-semibold text-admin-accent shadow-sm ring-1 ring-admin-accent/25"
                        : "text-admin-text/80 hover:bg-admin-surface/80 hover:text-admin-text"
                    }`}
                  >
                    {isActive && !collapsed ? (
                      <span className="absolute left-0 top-1.5 bottom-1.5 w-1 rounded-r-full bg-admin-accent" aria-hidden />
                    ) : null}
                    {Icon ? (
                      <span className="relative shrink-0">
                        <Icon className={`h-[18px] w-[18px] ${isActive ? "text-admin-accent" : "text-admin-muted"}`} aria-hidden />
                        {collapsed && countLabel ? (
                          <span className="absolute -right-2 -top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-admin-accent px-0.5 text-[9px] font-bold leading-none text-admin-accent-fg">
                            {countLabel}
                          </span>
                        ) : null}
                      </span>
                    ) : null}
                    <span className={collapsed ? "sr-only" : "min-w-0 flex-1 leading-snug"}>{item.label}</span>
                    {!collapsed && (countLabel || item.badge) ? (
                      <span className="ml-auto flex shrink-0 items-center gap-1">
                        {countLabel ? (
                          <span
                            className="flex h-5 min-w-5 items-center justify-center rounded-full bg-admin-accent px-1.5 text-[10px] font-bold tabular-nums leading-none text-admin-accent-fg"
                            aria-label={`${countLabel} ${countPhrase}`}
                          >
                            {countLabel}
                          </span>
                        ) : null}
                        {item.badge ? (
                          <span className="rounded border border-sky-200 bg-sky-50 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-sky-800">
                            {item.badge}
                          </span>
                        ) : null}
                      </span>
                    ) : null}
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </nav>
  );
}

function AdminSearch({ modules, onSelect, resetKey }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setQ("");
    setOpen(false);
  }, [resetKey]);

  const results = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return [];
    return modules
      .filter(
        (m) =>
          m.label.toLowerCase().includes(term) ||
          (m.badge && String(m.badge).toLowerCase().includes(term)) ||
          m.id.toLowerCase().includes(term) ||
          (m.groupTitle && m.groupTitle.toLowerCase().includes(term)) ||
          (m.keywords || []).some((k) => String(k).toLowerCase().includes(term))
      )
      .slice(0, 8);
  }, [q, modules]);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        document.getElementById("admin-global-search")?.focus();
        setOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div className="relative mx-auto w-full max-w-xl">
      <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
      <Input
        id="admin-global-search"
        value={q}
        onChange={(e) => {
          setQ(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder="Cerca qualcosa..."
        className="h-10 rounded-full border-slate-200 bg-slate-50 pl-10 pr-16 text-sm shadow-none focus-visible:ring-emerald-500/30"
      />
      <kbd className="pointer-events-none absolute right-3 top-1/2 hidden -translate-y-1/2 rounded-md border border-slate-200 bg-white px-1.5 py-0.5 text-[10px] font-medium text-slate-400 sm:inline">
        ⌘ K
      </kbd>
      {open && results.length > 0 ? (
        <div className="absolute left-0 right-0 z-40 mt-1 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg">
          {results.map((r) => {
            const Icon = r.icon;
            return (
              <button
                key={r.id}
                type="button"
                className="flex w-full items-center gap-2.5 px-3 py-2.5 text-left text-sm text-slate-700 hover:bg-slate-50"
                onMouseDown={(e) => {
                  e.preventDefault();
                  onSelect(r.id);
                  setQ("");
                  setOpen(false);
                }}
              >
                {Icon ? <Icon className="h-4 w-4 text-slate-400" /> : <Search className="h-3.5 w-3.5 text-slate-400" />}
                <span className="truncate">{r.label}</span>
                {r.groupTitle ? <span className="truncate text-xs text-slate-400">· {r.groupTitle}</span> : null}
                {r.badge ? (
                  <span className="ml-auto rounded bg-sky-500 px-1.5 py-0.5 text-[9px] font-bold uppercase text-white">
                    {r.badge}
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

export default function AdminLayout({
  navSections,
  activeModule,
  onModuleClick,
  adminUser,
  onLogout,
  impersonating = false,
  onEndImpersonation,
  twoFactorStatus,
  onConfigure2fa,
  pageTitle,
  pageIcon: PageIcon,
  breadcrumb = [],
  children,
  pushToken,
}) {
  const { effectiveTheme, toggleTheme } = useAppTheme();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    () => localStorage.getItem("admin-sidebar-collapsed") === "true"
  );
  const menuButtonRef = useRef(null);
  const drawerCloseRef = useRef(null);
  const [cdrptNotifications, setCdrptNotifications] = useState([]);

  useEffect(() => {
    const token = localStorage.getItem("super_admin_token");
    if (!token) return undefined;
    const load = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/notifiche?unread_only=true`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.ok) setCdrptNotifications((await response.json()).items || []);
      } catch { /* la campanella non blocca l'admin */ }
    };
    load();
    const timer = window.setInterval(load, 60000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    localStorage.setItem("admin-sidebar-collapsed", String(sidebarCollapsed));
  }, [sidebarCollapsed]);

  // Sotto lg (1024px) la sidebar è un drawer: Escape lo chiude e il focus torna al pulsante menu.
  useEffect(() => {
    if (!mobileMenuOpen) return undefined;
    const previous = document.activeElement;
    const menuButton = menuButtonRef.current;
    const onKey = (event) => {
      if (event.key === "Escape") setMobileMenuOpen(false);
    };
    document.addEventListener("keydown", onKey);
    // Focus sul pulsante chiudi del drawer (accessibilità tastiera).
    requestAnimationFrame(() => {
      drawerCloseRef.current?.focus();
      document.querySelector('#admin-mobile-drawer [aria-current="page"]')?.scrollIntoView({ block: "nearest" });
    });
    return () => {
      document.removeEventListener("keydown", onKey);
      if (previous && typeof previous.focus === "function") previous.focus();
      else menuButton?.focus();
    };
  }, [mobileMenuOpen]);

  const toggleSidebar = () => setSidebarCollapsed((current) => !current);

  const displayName = adminUser?.name || adminUser?.email || "Admin";
  const highlightPrimary = true;
  const showCommercialePush = Boolean(pushToken && adminReceivesWebsiteRequestPush(adminUser));
  const modules = useMemo(
    () => navSections.flatMap((s) => (s.items || []).map((item) => ({ ...item, groupTitle: s.title }))),
    [navSections]
  );

  const handleModuleClick = (id) => {
    onModuleClick(id);
    setMobileMenuOpen(false);
  };

  return (
    <div className="admin-shell min-h-screen bg-admin-bg p-3 text-admin-text md:p-4">
      <ThemeChoiceDialog />
      {impersonating ? (
        <div className="mb-3 flex flex-wrap items-center justify-center gap-2 rounded-xl bg-amber-500 px-4 py-2 text-center text-sm font-medium text-white shadow-sm">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          <span>
            Stai visualizzando come: {adminUser?.name || adminUser?.email || "utente admin"}
          </span>
          {onEndImpersonation ? (
            <button
              type="button"
              onClick={() => onEndImpersonation()}
              className="ml-1 underline hover:no-underline text-white/90 hover:text-white"
            >
              Torna al Super Admin
            </button>
          ) : null}
          <button
            type="button"
            onClick={() => {
              if (onEndImpersonation) onEndImpersonation({ closeTab: true });
              else window.close();
            }}
            className="underline hover:no-underline text-white/90 hover:text-white"
          >
            Chiudi scheda
          </button>
        </div>
      ) : null}
      {mobileMenuOpen ? (
        <div className="fixed inset-0 z-40 lg:hidden" role="dialog" aria-modal="true" aria-label="Menu amministrazione">
          <button
            type="button"
            className="absolute inset-0 bg-slate-900/40"
            aria-label="Chiudi menu"
            onClick={() => setMobileMenuOpen(false)}
          />
          <aside id="admin-mobile-drawer" className="absolute left-0 top-0 flex h-full w-72 max-w-[85vw] flex-col bg-admin-sidebar shadow-xl" aria-label="Menu">
            <button
              type="button"
              ref={drawerCloseRef}
              className="absolute right-3 top-4 z-10 rounded-lg p-1 text-slate-500 hover:bg-white/80"
              onClick={() => setMobileMenuOpen(false)}
              aria-label="Chiudi"
            >
              <X className="h-5 w-5" />
            </button>
            <SidebarBrand />
            <SidebarNav
              sections={navSections}
              activeModule={activeModule}
              onModuleClick={handleModuleClick}
              highlightPrimary={highlightPrimary}
            />
          </aside>
        </div>
      ) : null}

      <div className="flex h-[calc(100vh-1.5rem)] gap-[9px] md:h-[calc(100vh-2rem)] md:gap-3">
        <aside className={`relative z-20 hidden shrink-0 flex-col transition-[width] duration-200 lg:flex ${sidebarCollapsed ? "w-[68px]" : "w-[17rem]"}`}>
          <div className="flex h-full min-h-0 flex-col overflow-hidden rounded-tl-2xl rounded-bl-2xl rounded-tr-none rounded-br-none border border-r-0 border-admin-border bg-admin-sidebar shadow-[0_2px_8px_rgba(15,23,42,0.05)]">
            <SidebarBrand collapsed={sidebarCollapsed} />
            <SidebarNav
              sections={navSections}
              activeModule={activeModule}
              onModuleClick={handleModuleClick}
              collapsed={sidebarCollapsed}
              highlightPrimary={highlightPrimary}
            />
          </div>
          <div className="absolute right-0 top-[92px] z-30 flex h-9 w-9 translate-x-[12px] -translate-y-1/2 items-center justify-center">
            <span className="pointer-events-none absolute h-9 w-9 rounded-full bg-admin-bg" aria-hidden />
            <button
              type="button"
              onClick={toggleSidebar}
              className="relative flex h-6 w-6 items-center justify-center rounded-full border border-admin-border bg-white text-admin-muted shadow-sm transition hover:border-admin-accent hover:bg-admin-accent hover:text-admin-accent-fg"
              aria-label={sidebarCollapsed ? "Espandi menu" : "Riduci menu"}
              title={sidebarCollapsed ? "Espandi menu" : "Riduci menu"}
            >
              {sidebarCollapsed ? (
                <ChevronRight className="h-3.5 w-3.5" strokeWidth={2.5} />
              ) : (
                <ChevronLeft className="h-3.5 w-3.5" strokeWidth={2.5} />
              )}
            </button>
          </div>
        </aside>

        <div className="flex min-w-0 flex-1 flex-col overflow-hidden rounded-2xl border border-admin-border bg-admin-surface shadow-[0_1px_3px_rgba(15,23,42,0.05)] lg:rounded-tl-none lg:rounded-bl-none">
          {twoFactorStatus && !twoFactorStatus.enabled ? (
            <div className="shrink-0 border-b border-amber-200 bg-amber-50 px-4 py-2.5">
              <div className="flex flex-wrap items-center gap-3">
                <AlertTriangle className="h-5 w-5 shrink-0 text-amber-500" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-amber-800">
                    Proteggi il tuo account con l&apos;autenticazione a due fattori
                  </p>
                  <p className="mt-0.5 hidden text-xs text-amber-700 sm:block">
                    Riceverai un codice via email per ogni accesso.
                  </p>
                </div>
                <Button
                  size="sm"
                  className="shrink-0 bg-amber-600 text-white hover:bg-amber-700"
                  onClick={onConfigure2fa || onLogout}
                >
                  Configura 2FA
                </Button>
              </div>
            </div>
          ) : null}

          <header className="shrink-0 border-b border-admin-border">
            <div className="grid min-h-16 grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 gap-y-2 px-4 py-2 md:px-6 xl:grid-cols-[minmax(18rem,1fr)_minmax(12rem,22rem)_auto]">
              <div className="flex min-w-0 items-start gap-2">
                <Button
                  type="button"
                  ref={menuButtonRef}
                  variant="ghost"
                  size="sm"
                  className="mt-0.5 h-10 w-10 shrink-0 rounded-xl p-0 lg:hidden"
                  onClick={() => setMobileMenuOpen(true)}
                  aria-label="Apri menu"
                  aria-expanded={mobileMenuOpen}
                  aria-controls="admin-mobile-drawer"
                >
                  <Menu className="h-5 w-5" />
                </Button>

                <div className="hidden min-w-0 flex-1 items-start gap-3 sm:flex">
                  {PageIcon ? (
                    <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-admin-accent-soft">
                      <PageIcon className="h-5 w-5 text-admin-accent" aria-hidden />
                    </div>
                  ) : null}
                  <div className="min-w-0 flex-1">
                    <AdminBreadcrumb items={breadcrumb} />
                    <h1 className="whitespace-normal break-words text-lg font-semibold leading-snug text-admin-text">
                      {pageTitle || "Admin"}
                    </h1>
                  </div>
                </div>
              </div>

              <div className="order-3 col-span-2 min-w-0 xl:order-none xl:col-span-1">
                <AdminSearch modules={modules} onSelect={handleModuleClick} resetKey={activeModule} />
              </div>

              <div className="flex shrink-0 items-center justify-end gap-1 sm:gap-2">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" className="relative h-10 w-10 rounded-xl p-0" aria-label="Notifiche Pipeline CDRPT">
                      <Bell className="h-4 w-4" />
                      {cdrptNotifications.length ? <span className="absolute right-1 top-1 min-w-4 rounded-full bg-rose-600 px-1 text-[10px] font-bold text-white">{cdrptNotifications.length}</span> : null}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-80">
                    {cdrptNotifications.length ? cdrptNotifications.slice(0, 8).map((notification) => (
                      <DropdownMenuItem key={notification.id} className="items-start whitespace-normal" onClick={async () => {
                        const token = localStorage.getItem("super_admin_token");
                        await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/notifiche/mark-read`, { method: "POST", headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" }, body: JSON.stringify([notification.id]) });
                        setCdrptNotifications((rows) => rows.filter((row) => row.id !== notification.id));
                        const query = new URLSearchParams(notification.path.split("?")[1] || "");
                        onModuleClick("leads-admin");
                        window.history.replaceState({}, "", `/admin?${query}`);
                      }}>
                        <div><p className="font-medium">{notification.titolo}</p><p className="text-xs text-admin-muted">{notification.messaggio}</p></div>
                      </DropdownMenuItem>
                    )) : <DropdownMenuItem disabled>Nessuna notifica CDRPT</DropdownMenuItem>}
                  </DropdownMenuContent>
                </DropdownMenu>
                {twoFactorStatus ? (
                  twoFactorStatus.enabled ? (
                    <div className="hidden items-center gap-1 rounded-full bg-green-50 px-2.5 py-1 sm:flex">
                      <CheckCircle className="h-3.5 w-3.5 text-green-600" />
                      <span className="text-xs font-medium text-green-800">2FA</span>
                    </div>
                  ) : (
                    <div className="hidden items-center gap-1 rounded-full bg-amber-50 px-2.5 py-1 sm:flex">
                      <AlertTriangle className="h-3.5 w-3.5 text-amber-600" />
                      <span className="text-xs font-medium text-amber-800">2FA</span>
                    </div>
                  )
                ) : null}

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      className="flex h-10 items-center gap-2 px-2 md:px-3"
                      data-testid="admin-user-menu"
                    >
                      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-admin-accent text-sm font-semibold text-admin-accent-fg">
                        {getInitials(adminUser?.name, adminUser?.email)}
                      </div>
                      <span className="hidden max-w-[11rem] whitespace-normal break-words text-left text-sm font-medium leading-snug text-slate-700 lg:inline" title={scopeLabel(adminUser?.scope)}>
                        {scopeLabel(adminUser?.scope)}
                      </span>
                      <ChevronDown className="hidden h-4 w-4 shrink-0 text-slate-500 lg:block" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <div className="px-3 py-2">
                      <p className="text-sm font-medium text-slate-900">{displayName}</p>
                      <p className="text-xs text-slate-500">{adminUser?.email}</p>
                    </div>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={toggleTheme}
                      data-testid="theme-toggle"
                      aria-label={effectiveTheme === "dark" ? "Passa al tema chiaro" : "Passa al tema scuro"}
                    >
                      {effectiveTheme === "dark" ? <Sun className="mr-2 h-4 w-4" aria-hidden /> : <Moon className="mr-2 h-4 w-4" aria-hidden />}
                      {effectiveTheme === "dark" ? "Tema chiaro" : "Tema scuro"}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    {showCommercialePush ? <AdminPushMenuControls token={pushToken} /> : null}
                    {showCommercialePush ? <DropdownMenuSeparator /> : null}
                    <DropdownMenuItem
                      onClick={onLogout}
                      className="text-red-600"
                      data-testid="admin-menu-logout"
                    >
                      <LogOut className="mr-2 h-4 w-4" />
                      Esci
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          </header>

          <main className="admin-content page-transition min-h-0 flex-1 overflow-auto px-4 py-5 md:px-6 md:py-6 lg:px-8">
            <div className="mb-4 sm:hidden">
              <AdminBreadcrumb items={breadcrumb} className="mb-1" />
              <div className="flex items-center gap-2">
                {PageIcon ? <PageIcon className="h-5 w-5 text-admin-accent" aria-hidden /> : null}
                <h1 className="text-lg font-semibold text-admin-text">{pageTitle || "Admin"}</h1>
              </div>
            </div>
            {children}
          </main>
        </div>
      </div>
      {showCommercialePush ? <AdminPushNotificationPrompt token={pushToken} /> : null}
    </div>
  );
}
